// ============================================================================
// Time & Labor — the account manager's data layer.
//
// ⛔ EVERY READ HERE CHECKS `error` AND SAYS WHICH LIST FAILED. A refused read
// that becomes an empty list makes the screen lie twice: the list looks like it
// works and the company looks like it employs nobody (PLAYBOOK §15.3).
//
// ⛔ NO MONEY COLUMN IS SELECTED ANYWHERE IN THIS FILE, and a test asserts it.
// Who may read what is RLS's job — the five punch tables now carry the same
// `app_read` policy the rest of the module already had — so the scoping below
// is a VIEW filter for the person's benefit, exactly like useFacilityScope,
// not a security boundary pretending to be one.
// ============================================================================

import { startsOnEveryBuilding } from '../../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { useAuth } from '../../../shared/auth';
import type {
  HoursRow,
  NightManagerRow,
  NightRow,
  PunchPoint,
} from '../types-am';
import type { BuildingTimeVisit } from '../building-time';

/** "Facility Name (Owner or main cleaner)" — the standing naming rule. */
function placeOf(
  facilityId: string | null,
  names: Map<string, string>,
  market: string | null,
): string | null {
  if (facilityId) return names.get(facilityId) ?? facilityId;
  return market ? `Market: ${market}` : null;
}

async function facilityDisplayMap(): Promise<{ map: Map<string, string>; error: string | null }> {
  const map = new Map<string, string>();
  const [facRes, saRes] = await Promise.all([
    supabase.from('facilities').select('facility_id, facility_name, account_manager_eid'),
    supabase
      .from('service_assignments')
      .select('facility_id, provider_id, created_at')
      .eq('assignment_status', 'Active'),
  ]);
  if (facRes.error) return { map, error: errorMessage(facRes.error, 'Could not load the buildings.') };
  const provIds = Array.from(
    new Set(((saRes.data ?? []) as Array<{ provider_id: string | null }>)
      .map((r) => r.provider_id).filter((v): v is string => Boolean(v))),
  );
  const provRes = provIds.length
    ? await supabase.from('service_providers').select('provider_id, owner_name, partner_name').in('provider_id', provIds)
    : { data: [], error: null };
  if (provRes.error) return { map, error: errorMessage(provRes.error, 'Could not load the cleaners.') };
  const provName = new Map<string, string>();
  for (const p of (provRes.data ?? []) as Array<{ provider_id: string; owner_name: string | null; partner_name: string | null }>) {
    provName.set(p.provider_id, p.owner_name ?? p.partner_name ?? p.provider_id);
  }
  // Earliest active assignment wins, matching fn_time_facility_display.
  const firstProv = new Map<string, string>();
  const sorted = ((saRes.data ?? []) as Array<{ facility_id: string; provider_id: string | null; created_at: string }>)
    .slice().sort((a, b) => a.created_at.localeCompare(b.created_at));
  for (const a of sorted) {
    if (a.provider_id && !firstProv.has(a.facility_id)) firstProv.set(a.facility_id, a.provider_id);
  }
  for (const f of (facRes.data ?? []) as Array<{ facility_id: string; facility_name: string | null }>) {
    const owner = firstProv.get(f.facility_id);
    const suffix = owner ? ` (${provName.get(owner) ?? owner})` : '';
    map.set(f.facility_id, `${f.facility_name ?? f.facility_id}${suffix}`);
  }
  return { map, error: null };
}

/** The buildings this person is the account manager of. */
function useMyFacilityIds() {
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const [ids, setIds] = useState<Set<string>>(new Set());
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!eid) { setIds(new Set()); setResolved(true); return; }
    void (async () => {
      const { data, error: err } = await supabase
        .from('facilities').select('facility_id').eq('account_manager_eid', eid);
      if (cancelled) return;
      if (err) { setError(errorMessage(err, 'Could not work out which buildings are yours.')); setResolved(true); return; }
      setError(null);
      setIds(new Set((data ?? []).map((r) => (r as { facility_id: string }).facility_id)));
      setResolved(true);
    })();
    return () => { cancelled = true; };
  }, [eid]);
  return { myFacilityIds: ids, resolved, error };
}

// ---------------------------------------------------------------------------
// The night managers that report to me
// ---------------------------------------------------------------------------
export function useNightManagers() {
  const { employee, profile } = useAuth();
  const eid = employee?.employee_id ?? null;
  const role = profile?.role ?? 'none';
  const seesAll = startsOnEveryBuilding(role);

  const [rows, setRows] = useState<NightManagerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setError(null);
      // ⛔ The position is the thing. `role_type` marks what kind of position it
      // is, so nothing here string-matches a title.
      let q = supabase
        .from('positions')
        .select('position_id, title, market_region, filled_by_employee_id, reports_to_employee_id, notes, nm_building_checkins')
        .eq('role_type', 'Night Manager');
      if (!seesAll && eid) q = q.eq('reports_to_employee_id', eid);
      const posRes = await q.order('title');
      if (cancelled) return;
      if (posRes.error) {
        setError(errorMessage(posRes.error, 'Could not load the night-manager positions.'));
        setRows([]); setLoading(false); return;
      }
      type Pos = {
        position_id: string; title: string; market_region: string | null;
        filled_by_employee_id: string | null; reports_to_employee_id: string | null; notes: string | null;
        nm_building_checkins: boolean | null;
      };
      const positions = (posRes.data ?? []) as Pos[];
      const eids = positions.map((p) => p.filled_by_employee_id).filter((v): v is string => Boolean(v));

      const [empRes, wkRes] = await Promise.all([
        eids.length
          ? supabase.from('employees').select('employee_id, employee_name_first, employee_name_last').in('employee_id', eids)
          : Promise.resolve({ data: [], error: null }),
        eids.length
          // preferred_language rides along so the joining message HR copies is
          // written in the worker's own language, off their own record.
          ? supabase.from('workers').select('worker_id, employee_id, preferred_language').in('employee_id', eids)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (cancelled) return;
      if (empRes.error) { setError(errorMessage(empRes.error, 'Could not load the people filling them.')); setLoading(false); return; }
      if (wkRes.error) { setError(errorMessage(wkRes.error, 'Could not match them to the punch clock.')); setLoading(false); return; }

      const empName = new Map<string, string>();
      for (const e of (empRes.data ?? []) as Array<{ employee_id: string; employee_name_first: string | null; employee_name_last: string | null }>) {
        empName.set(e.employee_id, `${e.employee_name_first ?? ''} ${e.employee_name_last ?? ''}`.trim());
      }
      const workerOf = new Map<string, string>();
      const langOf = new Map<string, 'es' | 'en'>();
      for (const w of (wkRes.data ?? []) as Array<{ worker_id: string; employee_id: string | null; preferred_language: string | null }>) {
        if (w.employee_id) workerOf.set(w.employee_id, w.worker_id);
        langOf.set(w.worker_id, w.preferred_language === 'en' ? 'en' : 'es');
      }
      const workerIds = Array.from(workerOf.values());

      const [segRes, credRes] = await Promise.all([
        workerIds.length
          ? supabase.from('shift_segments').select('worker_id, facility_id, paid_minutes, shift_date').in('worker_id', workerIds)
          : Promise.resolve({ data: [], error: null }),
        // ⛔ Never read worker_credentials from the app: it is closed to every app
        // role, so this came back EMPTY and every night manager read "not yet" —
        // William included, nightly, since the screen was built. The picker's
        // has_pin reads a definer function and tells the truth (TCSD-0463).
        workerIds.length
          ? supabase.from('v_time_worker_picker').select('worker_id, has_pin').in('worker_id', workerIds)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (cancelled) return;
      if (segRes.error) { setError(errorMessage(segRes.error, 'Could not load their shifts.')); setLoading(false); return; }
      if (credRes.error) { setError(errorMessage(credRes.error, 'Could not tell who has a code yet.')); setLoading(false); return; }

      type Seg = { worker_id: string; facility_id: string | null; paid_minutes: number | null; shift_date: string };
      const segs = (segRes.data ?? []) as Seg[];
      const hasPin = new Set(
        ((credRes.data ?? []) as Array<{ worker_id: string; has_pin: boolean | null }>)
          .filter((c) => c.has_pin).map((c) => c.worker_id),
      );

      const out: NightManagerRow[] = positions.map((p) => {
        const wid = p.filled_by_employee_id ? workerOf.get(p.filled_by_employee_id) ?? null : null;
        const mine = wid ? segs.filter((s) => s.worker_id === wid) : [];
        return {
          position_id: p.position_id,
          title: p.title,
          market_region: p.market_region,
          filled_by_eid: p.filled_by_employee_id,
          filled_by_name: p.filled_by_employee_id ? empName.get(p.filled_by_employee_id) ?? null : null,
          worker_id: wid,
          worker_language: wid ? langOf.get(wid) ?? 'es' : 'es',
          reports_to_eid: p.reports_to_employee_id,
          nm_building_checkins: p.nm_building_checkins === true,
          has_pin: wid ? hasPin.has(wid) : false,
          nights: new Set(mine.map((s) => s.shift_date)).size,
          hours: Math.round((mine.reduce((t, s) => t + (s.paid_minutes ?? 0), 0) / 60) * 100) / 100,
          buildings: new Set(mine.map((s) => s.facility_id).filter(Boolean)).size,
          notes: p.notes,
        };
      });
      setRows(out); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [eid, seesAll, tick]);

  return { rows, loading, error, reload };
}

// ---------------------------------------------------------------------------
// One night manager's nights, each as a route
// ---------------------------------------------------------------------------
export function useNights(workerId: string | null) {
  const [rows, setRows] = useState<NightRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!workerId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    void (async () => {
      const { map, error: mapErr } = await facilityDisplayMap();
      if (cancelled) return;
      if (mapErr) { setError(mapErr); setLoading(false); return; }

      const [segRes, pRes, excRes] = await Promise.all([
        // The detail view is the same rows under the same row security, plus the
        // worker's clock (`site_tz`) that every time on this tab is shown on.
        supabase.from('v_time_segments_detail')
          .select('segment_id, worker_id, shift_date, started_at, ended_at, paid_minutes, segment_status, facility_id, market_region, site_tz')
          .eq('worker_id', workerId).order('shift_date', { ascending: false }),
        supabase.from('punch_events')
          .select('punch_event_id, event_type, event_at, facility_id, market_region, capture_mode, latitude, longitude, gps_accuracy_m, distance_from_site_m, geofence_result, supersedes_event_id, void_reason, created_by_eid')
          .eq('worker_id', workerId).order('event_at'),
        supabase.from('segment_exceptions')
          .select('exception_id, segment_id, rule_code, status').eq('worker_id', workerId).eq('status', 'open'),
      ]);
      if (cancelled) return;
      if (segRes.error) { setError(errorMessage(segRes.error, 'Could not load their nights.')); setLoading(false); return; }
      if (pRes.error) { setError(errorMessage(pRes.error, 'Could not load where they clocked.')); setLoading(false); return; }
      if (excRes.error) { setError(errorMessage(excRes.error, 'Could not load what needs attention.')); setLoading(false); return; }
      setError(null);

      type Seg = {
        segment_id: string; worker_id: string; shift_date: string;
        started_at: string | null; ended_at: string | null; paid_minutes: number | null;
        segment_status: string; facility_id: string | null; market_region: string | null;
        site_tz: string;
      };
      type Ev = PunchPoint & { market_region: string | null };
      const segs = (segRes.data ?? []) as Seg[];
      const evs = ((pRes.data ?? []) as Ev[]).map((e) => ({
        ...e, place_display: placeOf(e.facility_id, map, e.market_region),
      }));
      const openExc = new Map<string, string>();
      // The id as well as the rule, so the pinned banner can offer the door
      // that settles it rather than only naming the problem.
      const openExcId = new Map<string, string>();
      for (const e of (excRes.data ?? []) as Array<{ exception_id: string; segment_id: string; rule_code: string }>) {
        if (!openExc.has(e.segment_id)) {
          openExc.set(e.segment_id, e.rule_code);
          openExcId.set(e.segment_id, e.exception_id);
        }
      }

      const out: NightRow[] = segs.map((s) => {
        // The punches that belong to this night, by the night's own window.
        const from = s.started_at ? Date.parse(s.started_at) - 60 * 60 * 1000 : 0;
        const to = s.ended_at ? Date.parse(s.ended_at) + 60 * 60 * 1000 : Number.MAX_SAFE_INTEGER;
        const mine = evs.filter((e) => {
          const t = Date.parse(e.event_at);
          return t >= from && t <= to;
        });
        const ins = mine.filter((e) => e.event_type === 'in');
        const outs = mine.filter((e) => e.event_type === 'out');
        const distances = mine.map((e) => e.distance_from_site_m).filter((v): v is number => v != null);
        return {
          segment_id: s.segment_id,
          worker_id: s.worker_id,
          shift_date: s.shift_date,
          started_at: s.started_at,
          ended_at: s.ended_at,
          hours: Math.round(((s.paid_minutes ?? 0) / 60) * 100) / 100,
          paid_minutes: s.paid_minutes ?? 0,
          segment_status: s.segment_status,
          in_place: ins[0]?.place_display ?? placeOf(s.facility_id, map, s.market_region),
          out_place: outs.length ? outs[outs.length - 1].place_display : null,
          was_corrected: mine.some((e) => e.capture_mode === 'supervisor_manual'),
          furthest_m: distances.length ? Math.max(...distances) : null,
          open_exception: openExc.get(s.segment_id) ?? null,
          open_exception_id: openExcId.get(s.segment_id) ?? null,
          punches: mine,
          site_tz: s.site_tz,
        };
      });
      setRows(out); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [workerId, tick]);

  return { rows, loading, error, reload };
}

// ---------------------------------------------------------------------------
// The hours tab
// ---------------------------------------------------------------------------
export function useHours(fromDate: string, toDate: string, everyone: boolean) {
  const { myFacilityIds, resolved, error: scopeError } = useMyFacilityIds();
  const [rows, setRows] = useState<HoursRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!resolved || !fromDate || !toDate) return;
    setLoading(true);
    void (async () => {
      const { map, error: mapErr } = await facilityDisplayMap();
      if (cancelled) return;
      if (mapErr) { setError(mapErr); setLoading(false); return; }

      const segRes = await supabase.from('v_time_segments_detail')
        .select('segment_id, worker_id, facility_id, market_region, shift_date, started_at, ended_at, paid_minutes, segment_status, site_tz')
        .gte('shift_date', fromDate).lte('shift_date', toDate)
        .order('shift_date', { ascending: false });
      if (cancelled) return;
      if (segRes.error) { setError(errorMessage(segRes.error, 'Could not load the hours.')); setLoading(false); return; }

      type Seg = {
        segment_id: string; worker_id: string; facility_id: string | null; market_region: string | null;
        shift_date: string; started_at: string | null; ended_at: string | null;
        paid_minutes: number | null; segment_status: string; site_tz: string;
      };
      const segs = (segRes.data ?? []) as Seg[];
      const workerIds = Array.from(new Set(segs.map((s) => s.worker_id)));

      const [wkRes, excRes] = await Promise.all([
        workerIds.length
          ? supabase.from('workers').select('worker_id, first_name, preferred_name, last_name').in('worker_id', workerIds)
          : Promise.resolve({ data: [], error: null }),
        supabase.from('segment_exceptions').select('segment_id, rule_code').eq('status', 'open'),
      ]);
      if (cancelled) return;
      if (wkRes.error) { setError(errorMessage(wkRes.error, 'Could not load the people.')); setLoading(false); return; }
      if (excRes.error) { setError(errorMessage(excRes.error, 'Could not load what needs attention.')); setLoading(false); return; }
      setError(null);

      const name = new Map<string, string>();
      for (const w of (wkRes.data ?? []) as Array<{ worker_id: string; first_name: string; preferred_name: string | null; last_name: string }>) {
        name.set(w.worker_id, `${w.preferred_name ?? w.first_name} ${w.last_name}`);
      }
      const openExc = new Map<string, string>();
      for (const e of (excRes.data ?? []) as Array<{ segment_id: string; rule_code: string }>) {
        if (!openExc.has(e.segment_id)) openExc.set(e.segment_id, e.rule_code);
      }

      const all: HoursRow[] = segs.map((s) => ({
        segment_id: s.segment_id,
        worker_id: s.worker_id,
        worker_name: name.get(s.worker_id) ?? s.worker_id,
        facility_id: s.facility_id,
        place_display: placeOf(s.facility_id, map, s.market_region),
        shift_date: s.shift_date,
        started_at: s.started_at,
        ended_at: s.ended_at,
        hours: Math.round(((s.paid_minutes ?? 0) / 60) * 100) / 100,
        segment_status: s.segment_status,
        open_exception: openExc.get(s.segment_id) ?? null,
        mine: s.facility_id != null && myFacilityIds.has(s.facility_id),
        site_tz: s.site_tz,
      }));
      setRows(everyone ? all : all.filter((r) => r.mine));
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, everyone, resolved, myFacilityIds.size]);

  return { rows, loading, error: error ?? scopeError, myCount: myFacilityIds.size };
}


// ---------------------------------------------------------------------------
// Writes — existing doors only
// ---------------------------------------------------------------------------

/**
 * Issue or reset a clock-in code.
 *
 * ⛔ The code is returned to the caller ONCE so the manager can read it off the
 * screen and pass it on. Nothing in this app can text or email it. It is stored
 * only as a hash and can never be read back.
 */
export async function issueWorkerPin(workerId: string): Promise<string> {
  const pin = String(Math.floor(1000 + Math.random() * 9000));
  const { error } = await supabase.rpc('rpc_time_set_worker_pin', {
    p_worker_id: workerId,
    p_pin: pin,
  });
  if (error) throw error;
  return pin;
}

/**
 * The daily note links here carrying the item it is about, so the manager lands
 * on the right person rather than on a list. Resolves the item to its worker.
 *
 * ⛔ Reads `error` and says nothing rather than silently landing on the wrong
 * person: a refused read must not become "no such item".
 */
export function useWorkerOfException(exceptionId: string | null) {
  const [workerId, setWorkerId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!exceptionId) { setWorkerId(null); return; }
    void (async () => {
      const { data, error } = await supabase
        .from('segment_exceptions')
        .select('worker_id')
        .eq('exception_id', exceptionId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      setWorkerId((data as { worker_id: string | null }).worker_id);
    })();
    return () => { cancelled = true; };
  }, [exceptionId]);
  return workerId;
}

/**
 * The manager says what happened on an auto-closed night: they stand by the
 * full duration.
 *
 * ⛔ The EXISTING door (`rpc_time_staff_resolve_autoclose`), not a new one. It
 * refuses a night at a building that is not hers, and it refuses anything but
 * standing by the full time — changing the clock times is the correction door,
 * which already exists and already demands a reason.
 */
export async function confirmFullAutoclose(exceptionId: string, note: string | null): Promise<void> {
  const { error } = await supabase.rpc('rpc_time_staff_resolve_autoclose', {
    p_exception_id: exceptionId,
    p_confirm_full: true,
    p_note: note && note.trim() !== '' ? note.trim() : null,
  });
  if (error) throw error;
}

/** Helper for the screens: is this punch far enough out to be worth marking? */
export function useFarPunch(rows: NightRow[], thresholdM: number) {
  return useMemo(
    () => new Set(rows.filter((r) => (r.furthest_m ?? 0) > thresholdM).map((r) => r.segment_id)),
    [rows, thresholdM],
  );
}

// ---------------------------------------------------------------------------
// Night managers' building time (item #997, Ben 2026-10-27)
// ---------------------------------------------------------------------------

/**
 * The AM's setting on a night-manager position: Night only, or Night +
 * buildings. ⛔ Through its own door — the database refuses everyone but the
 * employee the position reports to and a superuser, and refuses a direct edit
 * of the column from any sign-in.
 */
export async function setNightManagerRecording(positionId: string, on: boolean): Promise<void> {
  const { error } = await supabase.rpc('rpc_time_set_nm_building_checkins', {
    p_position_id: positionId,
    p_on: on,
  });
  if (error) throw error;
}

/** The semimonthly pay periods up to today, newest first — read, never computed. */
export function usePayPeriods() {
  const [rows, setRows] = useState<Array<{ period_start: string; period_end: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const today = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const { data, error: err } = await supabase.from('pay_periods')
        .select('period_start, period_end')
        .eq('period_type', 'semimonthly_payroll')
        .lte('period_start', today)
        .order('period_start', { ascending: false })
        .limit(8);
      if (cancelled) return;
      if (err) { setError(errorMessage(err, 'Could not load the pay periods.')); return; }
      setError(null);
      setRows((data ?? []) as Array<{ period_start: string; period_end: string }>);
    })();
    return () => { cancelled = true; };
  }, []);
  return { rows, error };
}

/**
 * One night manager's building visits, minutes already worked out by the
 * database view (one rule for the panel and the night manager's summary).
 * ⛔ Row security decides who gets rows: superuser, executive, and the
 * employee the position reports to. Anyone else reads none.
 */
export function useBuildingVisits(workerId: string | null, enabled: boolean) {
  const [rows, setRows] = useState<BuildingTimeVisit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!workerId || !enabled) { setRows([]); setLoading(false); return; }
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase.from('v_building_visits')
        .select('visit_id, segment_id, facility_id, place_display, in_at, out_at, minutes')
        .eq('worker_id', workerId)
        .order('in_at');
      if (cancelled) return;
      if (err) { setError(errorMessage(err, 'Could not load the building check-ins.')); setLoading(false); return; }
      setError(null);
      setRows((data ?? []) as BuildingTimeVisit[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [workerId, enabled]);
  return { rows, loading, error };
}
